import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { IconButton } from "@mui/material";
import { setNotificationAtom } from "atoms";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTTS } from "shared/hooks/useTTS";

interface TTSPlayButtonProps {
  shouldStopPlayer: boolean;
  disabled?: boolean;
  selectedWord: string;
  alphabet: string;
  phoneme: string;
}

export default function TTSPlayButton({
  shouldStopPlayer,
  disabled = false,
  selectedWord,
  alphabet,
  phoneme,
}: TTSPlayButtonProps): JSX.Element {
  const { getTTSRequestUrl } = useTTS();
  const setNotification = useSetAtom(setNotificationAtom);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [disablePlayButton, setDisablePlayButton] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { t } = useTranslation();

  function stripProblemChars(ph: string): string {
    return ph.replace(/[\\`~ː"]/g, "");
  }
  useEffect(() => {
    if (audioPlayer) {
      void audioPlayer.play();
    }
  }, [audioPlayer]);

  useEffect(() => {
    if (hasError) {
      setNotification({
        message: errorMessage,
        severity: "error",
      });
      setHasError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const handlePlayClick = async () => {
    try {
      setDisablePlayButton(true);
      const selectedContent = `<speak><phoneme alphabet='${alphabet}' ph='${stripProblemChars(phoneme)}'>${selectedWord}</phoneme></speak>`;
      if (!selectedContent) {
        setDisablePlayButton(false);
        return;
      }

      const urlResult = await getTTSRequestUrl(false, "", selectedContent);
      const audioUrl =
        typeof urlResult === "string" ? urlResult : urlResult.url;
      const response = await fetch(audioUrl);

      if (!response.ok) {
        const json = await response.json();
        setHasError(true);
        const ttsErrorContent = json.detail.match(/tts: (.*)/)?.[1];
        setErrorMessage(`${t("tts.somethingWentWrong")}: ${ttsErrorContent}`);
        setDisablePlayButton(false);
        return;
      }

      const player = new Audio(audioUrl);
      player.onended = () => {
        setDisablePlayButton(false);
      };

      player.onerror = () => {
        setHasError(true);
        setErrorMessage(t("tts.generalErrorMessage"));
        setDisablePlayButton(false);
      };

      setAudioPlayer(player);
    } catch (error) {
      setHasError(true);
      setErrorMessage(t("tts.generalErrorMessage"));
      setDisablePlayButton(false);
    }
  };

  useEffect(() => {
    if (shouldStopPlayer && audioPlayer) {
      audioPlayer.pause();
      setAudioPlayer(null);
    }
  }, [audioPlayer, shouldStopPlayer]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
      }
    };
  }, [audioPlayer]);

  return (
    <IconButton
      onClick={() => void handlePlayClick()}
      disabled={disabled || disablePlayButton}
    >
      <PlayCircleOutlinedIcon
        sx={{
          color: disabled
            ? "action.disabled"
            : disablePlayButton
              ? "action.disabled"
              : "secondary.dark",
          width: 35,
          height: 35,
        }}
        data-testid="tts-play-button"
      />
    </IconButton>
  );
}
